/**
 * Online Food Ordering API Service
 * Axios instance with interceptors for auth and error handling
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/useAuthStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 - try token refresh
    if (error.response?.status === 401 && originalRequest) {
      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;

          // Update tokens in store
          useAuthStore.getState().updateTokens({
            accessToken: access_token,
            refreshToken: refresh_token,
          });

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - logout user
          useAuthStore.getState().logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - logout
        useAuthStore.getState().logout();
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH API
// ============================================

export const authApi = {
  register: (data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    marketing_email_consent?: boolean;
    marketing_sms_consent?: boolean;
  }) => api.post('/auth/register', data),

  login: (data: { email: string; password: string }) => api.post('/auth/login', data),

  // OTP (passwordless) authentication
  sendOtp: (email: string) => api.post('/auth/otp/send', { email }),

  verifyOtp: (email: string, code: string) => api.post('/auth/otp/verify', { email, code }),

  // OAuth - Google
  getGoogleOAuthUrl: (redirectUri: string) =>
    api.get('/auth/oauth/google/url', { params: { redirect_uri: redirectUri } }),

  googleOAuthCallback: (code: string, redirectUri: string) =>
    api.post('/auth/oauth/google/callback', { code, redirect_uri: redirectUri }),

  // OAuth - Facebook
  getFacebookOAuthUrl: (redirectUri: string) =>
    api.get('/auth/oauth/facebook/url', { params: { redirect_uri: redirectUri } }),

  facebookOAuthCallback: (code: string, redirectUri: string) =>
    api.post('/auth/oauth/facebook/callback', { code, redirect_uri: redirectUri }),

  refresh: (refreshToken: string) => api.post('/auth/refresh', { refresh_token: refreshToken }),

  logout: (refreshToken: string) => api.post('/auth/logout', { refresh_token: refreshToken }),

  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),

  getMe: () => api.get('/auth/me'),

  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string }) =>
    api.patch('/auth/me', data),

  updateConsents: (data: {
    marketing_email_consent?: boolean;
    marketing_sms_consent?: boolean;
    push_notification_consent?: boolean;
  }) => api.patch('/auth/me/consents', data),

  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/me/change-password', data),

  deleteAccount: () => api.delete('/auth/me'),

  // Addresses
  getAddresses: () => api.get('/auth/me/addresses'),

  createAddress: (data: {
    label?: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    postcode: string;
    delivery_instructions?: string;
    is_default?: boolean;
  }) => api.post('/auth/me/addresses', data),

  updateAddress: (
    id: string,
    data: Partial<{
      label: string;
      address_line_1: string;
      address_line_2: string;
      city: string;
      postcode: string;
      delivery_instructions: string;
      is_default: boolean;
    }>
  ) => api.patch(`/auth/me/addresses/${id}`, data),

  deleteAddress: (id: string) => api.delete(`/auth/me/addresses/${id}`),
};

// ============================================
// MENU API
// ============================================

export const menuApi = {
  getRestaurant: (slug?: string) =>
    api.get('/menu/restaurant', { params: slug ? { slug } : undefined }),

  getRestaurantStatus: (slug?: string) =>
    api.get('/menu/restaurant/status', { params: slug ? { slug } : undefined }),

  getCategories: (restaurantId?: string, lang = 'en') =>
    api.get('/menu/categories', {
      params: { ...(restaurantId ? { restaurant_id: restaurantId } : {}), lang },
    }),

  getCategory: (slug: string, restaurantId?: string, lang = 'en') =>
    api.get(`/menu/categories/${slug}`, {
      params: { ...(restaurantId ? { restaurant_id: restaurantId } : {}), lang },
    }),

  getItems: (params?: {
    category_id?: string;
    featured?: boolean;
    popular?: boolean;
    vegetarian?: boolean;
    vegan?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
    lang?: string;
  }) => api.get('/menu/items', { params }),

  getFeaturedItems: (limit = 8) => api.get('/menu/items/featured', { params: { limit } }),

  getPopularItems: (limit = 8) => api.get('/menu/items/popular', { params: { limit } }),

  getItem: (id: string) => api.get(`/menu/items/${id}`),

  getItemBySlug: (slug: string) => api.get(`/menu/items/slug/${slug}`),

  search: (q: string, limit = 20) => api.get('/menu/search', { params: { q, limit } }),
};

// ============================================
// ORDERS API
// ============================================

export const ordersApi = {
  create: (data: {
    restaurant_id: string;
    order_type: 'delivery' | 'collection';
    items: Array<{
      menu_item_id: string;
      name: string;
      quantity: number;
      unit_price: number;
      modifiers: Array<{
        group_id: string;
        option_id: string;
        name: string;
        price_adjustment: number;
      }>;
      notes?: string;
    }>;
    promo_code?: string;
    delivery_address_id?: string;
    delivery_address?: {
      address_line_1: string;
      address_line_2?: string;
      city: string;
      postcode: string;
      delivery_instructions?: string;
    };
    delivery_instructions?: string;
    guest_email?: string;
    guest_name?: string;
    guest_phone?: string;
    scheduled_for?: string;
  }) => api.post('/orders', data),

  list: (params?: { status?: string; limit?: number; offset?: number }) =>
    api.get('/orders', { params }),

  get: (id: string) => api.get(`/orders/${id}`),

  getByNumber: (orderNumber: string) => api.get(`/orders/number/${orderNumber}`),

  validatePromo: (data: {
    code: string;
    subtotal: number;
    order_type: 'delivery' | 'collection';
    is_first_order?: boolean;
  }) => api.post('/orders/validate-promo', data),

  calculateFees: (data: {
    subtotal: number;
    order_type: 'delivery' | 'collection';
    postcode?: string;
  }) => api.post('/orders/calculate-fees', data),

  repeat: (orderId: string, orderType: 'delivery' | 'collection') =>
    api.post(`/orders/${orderId}/repeat`, null, { params: { order_type: orderType } }),
};

// ============================================
// PAYMENTS API
// ============================================

export const paymentsApi = {
  createPaymentIntent: (orderId: string) =>
    api.post('/payments/create-payment-intent', { order_id: orderId }),

  processPayment: (data: { order_id: string; source_id: string; verification_token?: string }) =>
    api.post('/payments/process-payment', data),

  getPaymentIntentStatus: (orderId: string) => api.get(`/payments/payment-intent/${orderId}`),
};

// ============================================
// ADMIN API
// ============================================

export const adminApi = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),

  // Settings
  updateSettings: (data: {
    is_open?: boolean;
    accepts_delivery?: boolean;
    accepts_collection?: boolean;
    accepts_preorders?: boolean;
    prep_time_minutes?: number;
    service_fee_percent?: string;
    delivery_fee?: string;
    free_delivery_threshold?: string;
    minimum_order?: string;
    delivery_radius_miles?: string;
    hero_image_url?: string | null;
    logo_url?: string | null;
    name_translations?: Record<string, string> | null;
    address_translations?: Record<string, { address_line_1?: string; address_line_2?: string; city?: string }> | null;
    phone_translations?: Record<string, string> | null;
    promo_banner_text_translations?: Record<string, string> | null;
  }) => api.patch('/admin/settings', data),

  // Orders
  listOrders: (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/admin/orders', { params }),

  getOrder: (id: string) => api.get(`/admin/orders/${id}`),

  updateOrderStatus: (
    id: string,
    data: {
      status: string;
      notes?: string;
    }
  ) => api.patch(`/admin/orders/${id}/status`, data),

  // Categories
  listCategories: (restaurantId?: string) =>
    api.get('/admin/categories', {
      params: restaurantId ? { restaurant_id: restaurantId } : undefined,
    }),

  createCategory: (
    data: {
      name_translations: Record<string, string>;
      description?: string;
      description_translations?: Record<string, string>;
      display_order?: number;
      image_url?: string;
      is_active?: boolean;
    },
    restaurantId?: string
  ) =>
    api.post('/admin/categories', data, {
      params: restaurantId ? { restaurant_id: restaurantId } : undefined,
    }),

  updateCategory: (
    id: string,
    data: Partial<{
      name_translations: Record<string, string>;
      description: string | null;
      description_translations: Record<string, string>;
      image_url: string | null;
      display_order: number;
      is_active: boolean;
    }>
  ) => api.patch(`/admin/categories/${id}`, data),

  deleteCategory: (id: string) => api.delete(`/admin/categories/${id}`),

  // Menu Items
  createMenuItem: (data: {
    category_id: string;
    name_translations: Record<string, string>;
    description?: string;
    description_translations?: Record<string, string>;
    price: number;
    image_url?: string;
    allergens_translations?: Array<Record<string, string>>;
    is_vegetarian?: boolean;
    is_vegan?: boolean;
    is_halal?: boolean;
    is_spicy?: boolean;
    is_featured?: boolean;
    is_popular?: boolean;
    is_available?: boolean;
  }) => api.post('/admin/items', data),

  updateMenuItem: (
    id: string,
    data: Partial<{
      name_translations: Record<string, string>;
      description: string | null;
      description_translations: Record<string, string>;
      price: number;
      image_url: string | null;
      allergens_translations: Array<Record<string, string>>;
      is_vegetarian: boolean;
      is_vegan: boolean;
      is_halal: boolean;
      is_spicy: boolean;
      is_available: boolean;
      is_featured: boolean;
      is_popular: boolean;
    }>
  ) => api.patch(`/admin/items/${id}`, data),

  deleteMenuItem: (id: string) => api.delete(`/admin/items/${id}`),

  uploadItemImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/items/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Modifier Groups
  createModifierGroup: (
    itemId: string,
    data: {
      name_translations: Record<string, string>;
      description_translations?: Record<string, string>;
      modifier_type: 'single_required' | 'single_optional' | 'multi_optional';
      min_selections?: number;
      max_selections?: number;
    }
  ) => api.post(`/admin/items/${itemId}/modifier-groups`, data),

  updateModifierGroup: (
    groupId: string,
    data: Partial<{
      name_translations: Record<string, string>;
      description_translations: Record<string, string>;
      modifier_type: string;
      min_selections: number;
      max_selections: number;
      display_order: number;
      is_active: boolean;
    }>
  ) => api.patch(`/admin/modifier-groups/${groupId}`, data),

  deleteModifierGroup: (groupId: string) => api.delete(`/admin/modifier-groups/${groupId}`),

  // Modifier Options
  createModifierOption: (
    groupId: string,
    data: {
      name_translations: Record<string, string>;
      price_adjustment?: number;
      is_default?: boolean;
    }
  ) => api.post(`/admin/modifier-groups/${groupId}/options`, data),

  updateModifierOption: (
    optionId: string,
    data: Partial<{
      name_translations: Record<string, string>;
      price_adjustment: number;
      is_default: boolean;
      is_available: boolean;
      display_order: number;
    }>
  ) => api.patch(`/admin/modifier-options/${optionId}`, data),

  deleteModifierOption: (optionId: string) => api.delete(`/admin/modifier-options/${optionId}`),

  // Reorder
  reorderCategories: (order: string[]) => api.post('/admin/categories/reorder', order),

  reorderItems: (categoryId: string, order: string[]) =>
    api.post(`/admin/categories/${categoryId}/items/reorder`, order),

  // Promo Codes
  listPromos: (activeOnly = false) =>
    api.get('/admin/promos', { params: { active_only: activeOnly } }),

  createPromo: (data: {
    code: string;
    description?: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: number;
    minimum_order?: number;
    maximum_discount?: number;
    usage_limit?: number;
    per_user_limit?: number;
    valid_from: string;
    valid_until?: string;
    first_order_only?: boolean;
    delivery_only?: boolean;
    collection_only?: boolean;
  }) => api.post('/admin/promos', data),

  updatePromo: (
    id: string,
    data: {
      description?: string;
      discount_type?: 'percentage' | 'fixed';
      discount_value?: number;
      minimum_order?: number;
      maximum_discount?: number;
      usage_limit?: number;
      per_user_limit?: number;
      valid_from?: string;
      valid_until?: string;
      first_order_only?: boolean;
      delivery_only?: boolean;
      collection_only?: boolean;
      is_active?: boolean;
    }
  ) => api.patch(`/admin/promos/${id}`, data),

  deletePromo: (id: string) => api.delete(`/admin/promos/${id}`),

  // Translations
  updateItemTranslations: (
    id: string,
    data: {
      name_translations?: { ar?: string };
      description_translations?: { ar?: string };
    }
  ) => api.put(`/admin/menu/items/${id}/translations`, data),

  updateCategoryTranslations: (
    id: string,
    data: {
      name_translations?: { ar?: string };
      description_translations?: { ar?: string };
    }
  ) => api.put(`/admin/categories/${id}/translations`, data),

  autoTranslateItem: (id: string, targetLang = 'ar') =>
    api.post(`/admin/menu/items/${id}/auto-translate`, { target_lang: targetLang }),

  autoTranslateCategory: (id: string, targetLang = 'ar') =>
    api.post(`/admin/categories/${id}/auto-translate`, { target_lang: targetLang }),

  batchAutoTranslate: (scope: 'all' | 'missing' = 'missing', targetLang = 'ar') =>
    api.post('/admin/menu/batch-auto-translate', { target_lang: targetLang, scope }),

  translateText: (text: string, targetLang = 'ar') =>
    api.post('/admin/translate-text', { text, target_lang: targetLang }),

  updateModifierGroupTranslations: (
    groupId: string,
    data: { name_translations?: { ar?: string } }
  ) => api.put(`/admin/menu/modifiers/${groupId}/translations`, data),

  updateModifierOptionTranslations: (
    optionId: string,
    data: { name_translations?: { ar?: string } }
  ) => api.put(`/admin/menu/modifier-options/${optionId}/translations`, data),

  // Audit Logs
  listAuditLogs: (params?: {
    action?: string;
    entity_type?: string;
    user_id?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }) => api.get('/admin/audit-logs', { params }),

  getAuditLog: (id: string) => api.get(`/admin/audit-logs/${id}`),

  // Notification Settings
  getNotificationSettings: () => api.get('/admin/notifications'),

  updateNotificationSettings: (data: {
    order_confirmed?: boolean;
    order_preparing?: boolean;
    order_ready?: boolean;
    order_out_for_delivery?: boolean;
    order_delivered?: boolean;
    order_cancelled?: boolean;
    admin_new_order?: boolean;
    admin_emails?: string[];
  }) => api.patch('/admin/notifications', data),
};

// ============================================
// UPLOADS API
// ============================================

export const uploadsApi = {
  uploadImage: (file: File, folder = 'images') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/uploads/image?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteImage: (key: string) => api.delete('/uploads/image', { params: { key } }),

  getConfig: () => api.get('/uploads/config'),
};

// ============================================
// CHAT API
// ============================================

export const chatApi = {
  sendMessage: (
    message: string,
    history: Array<{ role: 'user' | 'model'; content: string }>,
    language: 'en' | 'ar' = 'en',
    customerName?: string,
    cart?: Array<{ menu_item_id: string; name: string; quantity: number }>
  ) =>
    api.post('/chat/message', {
      message,
      history,
      language,
      customer_name: customerName,
      cart: cart ?? [],
    }),

  validateAddress: (postcode: string, subtotal = 0) =>
    api.post('/chat/validate-address', { postcode, subtotal }),
};

export default api;
